import React from 'react';
import { formatUsd } from '@/lib/estimatesData';

/* ============================================================================
 * ProposalChart — dependency-free SVG charts for the proposal (bar / line /
 * donut). Hand-rolled SVG so it prints crisply and needs no chart library.
 * Colors come from the theme's chart tokens so it works in light + dark and
 * on the cream/print sheet.
 * ========================================================================== */

const PALETTE = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
];

const fmt = (v, format) => (format === 'money' ? formatUsd(v) : `${v}`);

const BarChart = ({ data, format }) => {
  const W = 520;
  const H = 240;
  const padX = 28;
  const padTop = 24;
  const padBottom = 46;
  const max = Math.max(1, ...data.map((d) => d.value));
  const n = data.length;
  const slot = (W - padX * 2) / Math.max(1, n);
  const barW = Math.min(64, slot * 0.6);
  const plotH = H - padTop - padBottom;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img">
      {/* baseline */}
      <line
        x1={padX}
        y1={H - padBottom}
        x2={W - padX}
        y2={H - padBottom}
        stroke="hsl(var(--border))"
        strokeWidth="1"
      />
      {data.map((d, i) => {
        const h = (d.value / max) * plotH;
        const x = padX + slot * i + (slot - barW) / 2;
        const y = H - padBottom - h;
        return (
          <g key={i}>
            <rect
              x={x}
              y={y}
              width={barW}
              height={Math.max(0, h)}
              rx="2"
              fill="hsl(var(--primary))"
            />
            <text
              x={x + barW / 2}
              y={y - 6}
              textAnchor="middle"
              fontSize="11"
              fontWeight="600"
              fill="hsl(var(--foreground))"
            >
              {fmt(d.value, format)}
            </text>
            <text
              x={x + barW / 2}
              y={H - padBottom + 16}
              textAnchor="middle"
              fontSize="10"
              fill="hsl(var(--muted-foreground))"
            >
              {d.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

const LineChart = ({ data, format }) => {
  const W = 520;
  const H = 240;
  const padX = 34;
  const padTop = 24;
  const padBottom = 46;
  const max = Math.max(1, ...data.map((d) => d.value));
  const n = data.length;
  const plotH = H - padTop - padBottom;
  const step = n > 1 ? (W - padX * 2) / (n - 1) : 0;
  const pt = (d, i) => ({
    x: padX + step * i,
    y: H - padBottom - (d.value / max) * plotH,
  });
  const pts = data.map(pt);
  const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img">
      <line
        x1={padX}
        y1={H - padBottom}
        x2={W - padX}
        y2={H - padBottom}
        stroke="hsl(var(--border))"
        strokeWidth="1"
      />
      <path d={path} fill="none" stroke="hsl(var(--primary))" strokeWidth="2.5" />
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="3.5" fill="hsl(var(--primary))" />
          <text
            x={p.x}
            y={p.y - 9}
            textAnchor="middle"
            fontSize="10"
            fontWeight="600"
            fill="hsl(var(--foreground))"
          >
            {fmt(data[i].value, format)}
          </text>
          <text
            x={p.x}
            y={H - padBottom + 16}
            textAnchor="middle"
            fontSize="10"
            fill="hsl(var(--muted-foreground))"
          >
            {data[i].label}
          </text>
        </g>
      ))}
    </svg>
  );
};

const DonutChart = ({ data, format }) => {
  const total = data.reduce((a, d) => a + d.value, 0) || 1;
  const R = 70;
  const r = 42;
  const cx = 90;
  const cy = 90;
  let acc = 0;
  const arc = (frac0, frac1) => {
    const a0 = frac0 * 2 * Math.PI - Math.PI / 2;
    const a1 = frac1 * 2 * Math.PI - Math.PI / 2;
    const large = frac1 - frac0 > 0.5 ? 1 : 0;
    const x0 = cx + R * Math.cos(a0);
    const y0 = cy + R * Math.sin(a0);
    const x1 = cx + R * Math.cos(a1);
    const y1 = cy + R * Math.sin(a1);
    const xi1 = cx + r * Math.cos(a1);
    const yi1 = cy + r * Math.sin(a1);
    const xi0 = cx + r * Math.cos(a0);
    const yi0 = cy + r * Math.sin(a0);
    return `M${x0},${y0} A${R},${R} 0 ${large} 1 ${x1},${y1} L${xi1},${yi1} A${r},${r} 0 ${large} 0 ${xi0},${yi0} Z`;
  };

  return (
    <div className="flex items-center gap-5 flex-wrap">
      <svg viewBox="0 0 180 180" className="w-40 h-40 shrink-0" role="img">
        {data.map((d, i) => {
          const f0 = acc / total;
          acc += d.value;
          const f1 = acc / total;
          return (
            <path key={i} d={arc(f0, f1)} fill={PALETTE[i % PALETTE.length]} />
          );
        })}
      </svg>
      <ul className="space-y-1.5">
        {data.map((d, i) => (
          <li key={i} className="flex items-center gap-2 text-sm">
            <span
              className="inline-block h-3 w-3 rounded-sm shrink-0"
              style={{ background: PALETTE[i % PALETTE.length] }}
            />
            <span className="text-foreground/90">{d.label}</span>
            <span className="text-muted-foreground tabular-nums">
              {fmt(d.value, format)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const ProposalChart = ({ block }) => {
  const data = (block?.data || []).filter(
    (d) => d.label || Number(d.value) > 0
  );
  if (data.length === 0) return null;
  const format = block?.valueFormat === 'money' ? 'money' : 'plain';

  return (
    <figure className="print-avoid" data-testid="scope-chart">
      {block.title ? (
        <figcaption className="mb-2 text-sm font-semibold text-foreground">
          {block.title}
        </figcaption>
      ) : null}
      <div className="rounded-md border border-border p-3 bg-[hsl(var(--card))]">
        {block.chartType === 'line' ? (
          <LineChart data={data} format={format} />
        ) : block.chartType === 'donut' ? (
          <DonutChart data={data} format={format} />
        ) : (
          <BarChart data={data} format={format} />
        )}
      </div>
    </figure>
  );
};

export default ProposalChart;
